import { useEffect, useState } from 'react';
import { type Block, type Stream } from '../api/client.ts';
import { t } from '../i18n/index.ts';
import { formatDateKey, formatDateLabel } from '../lib/format.ts';
import { BlockRow } from './BlockRow.tsx';
import { BlockEditor } from './BlockEditor.tsx';

/** Countdowns tick without refetching. */
const CLOCK_MS = 1_000;

interface Props {
  stream: Stream;
  blocks: Block[] | null;
  loadFailed: boolean;
  timeZone: string;
  onEdit: (block: Block) => void;
  editingBlockStart: string | null;
  onCloseEditor: () => void;
}

export function BlockGrid({
  stream, blocks, loadFailed, timeZone, onEdit, editingBlockStart, onCloseEditor,
}: Props): React.ReactElement {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), CLOCK_MS);
    return () => clearInterval(timer);
  }, []);

  if (loadFailed && blocks === null) {
    return <div className="empty">{t('error.network')}</div>;
  }
  if (blocks === null) return <div className="empty">{t('player.loading')}</div>;
  if (blocks.length === 0) return <div className="empty">{t('empty.no_recordings')}</div>;

  // Group under a date divider so a grid spanning midnight stays readable.
  const groups: Array<{ key: string; blocks: Block[] }> = [];
  for (const block of blocks) {
    const key = formatDateKey(block.block_start_at, timeZone);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.blocks.push(block);
    else groups.push({ key, blocks: [block] });
  }

  return (
    <section>
      <div className="grid-head">
        <h2>{t('blocks.title', { hours: stream.retention_hours })}</h2>
      </div>

      {groups.map((group) => (
        <div key={group.key}>
          <div className="date-divider">
            {formatDateLabel(group.blocks[0]!.block_start_at, timeZone, new Date(now))}
          </div>
          <div className="blocks">
            {group.blocks.map((block) => (
              // The editor opens in place, under the row it belongs to. Lifting
              // the block to the top of the list moved the thing the producer
              // was looking at out from under them.
              <div key={block.block_start_at}>
                <BlockRow
                  block={block}
                  timeZone={timeZone}
                  now={now}
                  onEdit={onEdit}
                  editingBlockStart={editingBlockStart}
                />
                {editingBlockStart === block.block_start_at && (
                  <BlockEditor
                    block={block}
                    streamId={stream.id}
                    timeZone={timeZone}
                    onClose={onCloseEditor}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}
